import { useState } from "react";

import { addYears } from "date-fns";
import { CalendarDays } from "lucide-react";

import { DateTimeInputV2 } from "@/components/pieces/DateTimeInputV2";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogInner,
  DialogTitle,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { SUBTEXT_COLOR } from "@/css/constants";
import { getBrowserTimezone } from "@/utils/datetime";

interface EditModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSave?: (settings: EditSettings) => void;
  initialData?: EditSettings;
}

export interface EditSettings {
  setDueDate: boolean;
  dueDate: string;
}

export function EditModal({
  open,
  onOpenChange,
  onSave,
  initialData,
}: EditModalProps) {
  const [settings, setSettings] = useState<EditSettings>(
    initialData || {
      setDueDate: true,
      dueDate: "Oct 2, 2025",
    },
  );

  const [dueDate, setDueDate] = useState<Date | undefined>(undefined);
  const [timezone, setTimezone] = useState(getBrowserTimezone());
  const maxDate = addYears(new Date(), 1);
  const minDate = new Date();

  const handleSave = () => {
    onSave?.(settings);
    onOpenChange(false);
  };

  const handleCheckboxChange = (checked: boolean) => {
    setSettings((prev) => ({
      ...prev,
      setDueDate: checked,
    }));
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="w-[550px] border border-gray-200 rounded-lg">
        {/* Header */}
        <DialogHeader>
          <DialogTitle>Edit Assignment</DialogTitle>
        </DialogHeader>

        {/* Content */}
        <DialogInner className="space-y-6">
          {/* Due Date Toggle */}
          <div className="flex items-center gap-2">
            <Checkbox
              checked={settings.setDueDate}
              onCheckedChange={handleCheckboxChange}
              className="h-5 w-5"
            />
            <span
              className="text-sm text-gray-900 leading-relaxed clickable select-none"
              onClick={() =>
                setSettings((prev) => ({
                  ...prev,
                  setDueDate: !prev.setDueDate,
                }))
              }
            >
              Set a due date for this assignment
            </span>
          </div>

          {/* Date/Time Picker */}
          {settings.setDueDate && (
            <div className="space-y-4">
              <div className="space-y-4">
                <Label className="text-base font-semibold mb-1">Due Date</Label>
                <div className="flex gap-3 w-full">
                  <div style={{ position: "relative", width: "100%" }}>
                    <CalendarDays
                      style={{
                        position: "absolute",
                        top: 12,
                        left: 10,
                        zIndex: 99,
                        color: SUBTEXT_COLOR,
                      }}
                      strokeWidth={1.5}
                    />
                    <DateTimeInputV2
                      onChange={(date) => {
                        setDueDate(date ? new Date(date) : undefined);
                      }}
                      value={dueDate?.toISOString() ?? null}
                      timezone={timezone}
                      onChangeTimezone={setTimezone}
                      maxDateTime={maxDate}
                      minDateTime={minDate}
                      containerStyles={{
                        height: "50px",
                        width: "100%",
                      }}
                    />
                  </div>
                </div>
              </div>
            </div>
          )}
        </DialogInner>
        <DialogFooter>
          {/* Footer */}
          <Button
            intent="primary"
            size="sm"
            onClick={handleSave}
            className="w-[100px] h-10"
          >
            Save
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
